import { localStorageKeys } from '@/utils/tokens';
import { saveFilterToLocalStorage } from '@/utils/localStorage';

// Only the server-side scope survives a reload; the client-side SoC, parking
// and vehicle-id filters are session-only.
export const WASHING_PERSISTED_FILTER_KEYS = [
  'contracts',
  'flattenContracts',
  'depots',
] as const;

export type PersistedWashingFilterKey =
  (typeof WASHING_PERSISTED_FILTER_KEYS)[number];

export type PersistedWashingFilters = Record<
  PersistedWashingFilterKey,
  string[][]
>;

const NUMERIC_ID = /^\d+$/;

const isStringPath = (value: unknown): value is string[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every((segment) => typeof segment === 'string');

// A depot path is always [contractId, monitoredDepotId].
const isDepotPath = (value: unknown): value is string[] =>
  isStringPath(value) &&
  value.length === 2 &&
  value.every((segment) => NUMERIC_ID.test(segment));

const readPaths = (
  value: unknown,
  isPath: (path: unknown) => path is string[]
): string[][] | undefined =>
  Array.isArray(value) && value.every(isPath) ? value : undefined;

/**
 * Reads the persisted contract/depot scope. The payload may predate the
 * current shape or be hand-edited, so any field that is not a list of valid
 * paths is dropped and the slice default applies instead.
 */
export const readPersistedWashingFilters =
  (): Partial<PersistedWashingFilters> => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(
        localStorage.getItem(localStorageKeys.washingFilters) ?? '{}'
      );
    } catch {
      return {};
    }
    if (typeof parsed !== 'object' || parsed === null) {
      return {};
    }

    const record = parsed as Record<string, unknown>;
    const result: Partial<PersistedWashingFilters> = {};
    const contracts = readPaths(record.contracts, isStringPath);
    const flattenContracts = readPaths(record.flattenContracts, isStringPath);
    const depots = readPaths(record.depots, isDepotPath);
    if (contracts) {
      result.contracts = contracts;
    }
    if (flattenContracts) {
      result.flattenContracts = flattenContracts;
    }
    if (depots) {
      result.depots = depots;
    }
    return result;
  };

export const persistWashingFilters = (state: PersistedWashingFilters) => {
  saveFilterToLocalStorage(localStorageKeys.washingFilters, state, [
    ...WASHING_PERSISTED_FILTER_KEYS,
  ]);
};
